import React from 'react'
import { ESG_COPY } from '@/locales/en'
import { InfoIconTooltip } from '@/common/ui/tooltip'
import { H2 } from './h2'

function Chip({ children, positive }: { children: React.ReactNode; positive?: boolean }) {
    const base =
        'px-3 py-1.5 rounded-2xl backdrop-blur-[5px] flex justify-start items-center gap-1 overflow-hidden max-w-fit'
    const positiveClasses = 'bg-brand-accent-soft-lime text-brand-primary-forest'
    const neutralClasses = 'bg-primary-dust text-brand-primary-forest'
    const negativeClasses = 'bg-primary-dust text-brand-primary-forest'
    return (
        <div
            className={`${base} ${positive ? positiveClasses : positive === false ? negativeClasses : neutralClasses}`}
        >
            {children}
        </div>
    )
}

type AvoidedItem = { avoided: string }
type PositiveItem = { positiveThemesIncluded: string }

export function EthicalTagsPanel({
    description,
    positive,
    avoided,
    onTooltipOpen,
}: {
    description?: string
    positive?: PositiveItem[]
    avoided?: AvoidedItem[]
    onTooltipOpen?: (open: boolean) => void
}) {
    const pos = (positive || []).map((p) => ({ tag: (p.positiveThemesIncluded || '').trim() })).filter((p) => p.tag)
    const avd = (avoided || []).map((p) => ({ tag: (p.avoided || '').trim() })).filter((p) => p.tag)

    if (pos.length === 0 && avd.length === 0) return null

    return (
        <div className="w-full self-stretch inline-flex flex-col justify-start items-start gap-4 lg:gap-6">
            <div className="self-stretch inline-flex flex-col justify-start items-start gap-4">
                <H2 className="justify-center text-black text-3xl md:text-3xl font-normal font-['Serious Sans'] mb-0 leading-8">
                    {ESG_COPY.title}
                </H2>
                {description && (
                    <div className="self-stretch justify-start text-content-secondary text-sm font-normal font-['Inter'] leading-5">
                        {description}
                    </div>
                )}
            </div>

            {pos.length > 0 && (
                <div className="self-stretch inline-flex flex-col justify-start items-start gap-4">
                    <div className="justify-center text-black text-sm font-medium font-['Inter'] leading-5">
                        {ESG_COPY.positiveThemesIncluded.replace('{count}', String(pos.length))}
                    </div>

                    <div className="self-stretch inline-flex justify-start items-start gap-2 flex-wrap content-start">
                        {pos.map((t) => (
                            <Chip key={`pos-${t.tag}`} positive>
                                <div className="justify-center text-brand-primary-forest text-xs font-normal font-['Inter'] leading-4">
                                    {t.tag}
                                </div>
                            </Chip>
                        ))}
                    </div>
                </div>
            )}

            {avd.length > 0 && (
                <>
                    {pos.length > 0 && (
                        <div className="self-stretch h-0 outline outline-1 outline-offset-[-0.50px] outline-border-divider"></div>
                    )}
                    <div className="self-stretch inline-flex flex-col justify-start items-start gap-4">
                        <div className="flex items-center gap-2">
                            <div className="justify-center text-black text-sm font-medium font-['Inter'] leading-5">
                                {ESG_COPY.issuesAvoided.replace('{count}', String(avd.length))}
                            </div>
                            <InfoIconTooltip onOpenChange={onTooltipOpen}>
                                <div className="self-stretch gap-4 w-60">
                                    <div className="self-stretch justify-start text-content-primary text-sm font-medium font-['Inter'] leading-5">
                                        {ESG_COPY.issuesAvoidedTooltip.title}
                                    </div>
                                    <div className="self-stretch justify-start text-content-primary text-xs font-normal font-['Inter'] leading-5">
                                        {ESG_COPY.issuesAvoidedTooltip.description}
                                    </div>
                                </div>
                            </InfoIconTooltip>
                        </div>
                        <div className="self-stretch inline-flex justify-start items-start gap-2 flex-wrap content-start">
                            {avd.map((t) => (
                                <Chip key={`avoided-${t.tag}`} positive={false}>
                                    <div className="justify-center text-brand-primary-forest text-xs font-normal font-['Inter'] leading-4">
                                        {t.tag}
                                    </div>
                                </Chip>
                            ))}
                        </div>
                    </div>
                </>
            )}
        </div>
    )
}
